import { OwcEditableDropdown, OwcListItem } from "@one/react";
import { react18WorkaroundProps } from "../utils/constants/constants";
import { FC, memo } from "react";
import { formatUserDisplay, USER_FORMAT_PRESETS } from "../utils/helpers/text/userFormatting";

interface User {
  userId: string;
  email: string;
  name?: string;
  givenName?: string;
  familyName?: string;
  label?: string;
}

interface SigneeOption {
  name: string;
  label: string;
}

interface SigneeFilterProps {
  signeeFilter: '' | User | SigneeOption;
  signeeOptions: SigneeOption[];
  onSigneeFilterChange: (selectedSignee: SigneeOption) => void;
  width?: number;
  label?: string;
  placeholder?: string;
  dataTestid?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
}

export const SigneeFilter: FC<SigneeFilterProps> = memo(({
  signeeFilter,
  signeeOptions,
  onSigneeFilterChange,
  width = 200,
  label = "Signee",
  placeholder = "Type to search users",
  dataTestid = "signee-filter-dropdown",
  disabled = false,
  style = {}
}) => {
  const getDisplayValue = (): string => {
    if (signeeFilter === "") {
      return "All users";
    }

    if (typeof signeeFilter === 'object') {
      if ('label' in signeeFilter && signeeFilter.label) {
        return signeeFilter.label;
      }
      if ('userId' in signeeFilter && signeeFilter.userId) {
        return formatUserDisplay(signeeFilter as User, USER_FORMAT_PRESETS.default);
      }
    }

    return "All users";
  };

  const isOptionSelected = (option: SigneeOption): boolean => {
    if (signeeFilter === "" && option.name === "") {
      return true;
    }

    if (typeof signeeFilter === 'object' && 'userId' in signeeFilter) {
      return (signeeFilter as User).userId === option.name;
    }

    return false;
  };

  const handleSelectChange = (ev: any) => {
    if (ev.detail !== null && ev.detail !== undefined) {
      const selectedOption = signeeOptions[ev.detail];
      if (selectedOption) {
        onSigneeFilterChange(selectedOption);
      }
    } else {
      // User clicked the clear icon, reset to "All users"
      onSigneeFilterChange({ name: "", label: "All users" });
    }
  };

  const handleInputKeyDown = (ev: any) => {
    const inputValue = ev.target?.value || "";
    if (inputValue.trim() === "" && (ev.detail === "Enter" || ev.detail === "Tab")) {
      onSigneeFilterChange({ name: "", label: "All users" });
    }
  };


  return (
    <OwcEditableDropdown
      style={{ ...style, width }}
      label={label}
      data-testid={dataTestid}
      name="signeeFilter"
      value={getDisplayValue()}
      {...react18WorkaroundProps}
      placeholder={placeholder}
      disabled={disabled}
      onSelectChange={handleSelectChange}
      onInputKeyDown={handleInputKeyDown}
    >
      {signeeOptions.map((option) => (
        <OwcListItem
          key={option.name || "all-users"}
          value={option.label}
          selected={isOptionSelected(option)}
          data-testid={"signee-filter-menu-item-" + (option.name || "all")}
          {...react18WorkaroundProps}
        >
          {option.label}
        </OwcListItem>
      ))}
    </OwcEditableDropdown>
  )
    ;
});
